import React, { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  Timestamp,
  getDoc,
  where,
  arrayUnion,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import { RouteGroup, Route, Client, Order, InventoryItem, Transportista, OrderItem } from '../models';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Paper,
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Tooltip,
  FormGroup,
  TablePagination,
  TableSortLabel,
  Card,
  CardContent,
  CardActions,
  Avatar,
  SvgIcon,
  SvgIconProps,
  Chip,
  Switch,
  useTheme,
  useMediaQuery,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday,
  Today,
  PendingActions,
  CheckCircle,
  Cancel,
  FilterList as FilterListIcon,
  ViewColumn as ViewColumnIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  GetApp as GetAppIcon,
  FormatListBulleted,
  CalendarMonth,
  CalendarViewMonth,
  NavigateNext,
  CalendarViewWeek,
  DriveFileRenameOutline,
} from '@mui/icons-material';
import OrderCreationModalAutomatic from './OrderCreationModalAutomatic';
import OrderCreationModalClasicAutomatic from './OrderCreationModalClasicAutomatic';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import CloseIcon from '@mui/icons-material/Close';
import { es, id } from 'date-fns/locale';
import { Person as PersonIcon } from '@mui/icons-material';

interface OrderCreationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (order: Order) => Promise<void>;
  inventoryItems: InventoryItem[];
  initialOrder?: Order;
}

const columnNames = {
  name: 'Nombre',
  routeType: 'Tipo de Ruta',
  dayOfWeek: 'Día de la Semana',
  weekOfMonth: 'Semana del Mes',
  createdAt: 'Fecha de Inicio',
  nextDelivery: 'Siguiente Entrega'
};


// Define styled components
const DarkOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(4px)',
  zIndex: 9998,
  animation: 'fadeIn 0.3s ease-out',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
});

const NotificationPopup = styled(Box)({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  padding: '32px 48px',
  borderRadius: '24px',
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.25)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  zIndex: 9999,
  animation: 'slideIn 0.5s ease-out',
  '@keyframes slideIn': {
    '0%': {
      transform: 'translate(-50%, -40%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translate(-50%, -50%)',
      opacity: 1,
    },
  },
});

const AnimatedEmoji = styled('span')({
  display: 'inline-block',
  animation: 'bounce 1s ease infinite',
  fontSize: '2.5rem',
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-8px)',
    },
  },
});

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#00b4d8',
    },
    '&:hover fieldset': {
      borderColor: '#00b4d8',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00b4d8',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#00b4d8',
  },
}));

const InfoSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: '#f5f5f5',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  color: '#00b4d8',
}));

const IconWrapper = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${theme.breakpoints.down('sm')}`]: {
    display: 'none',
  },
}));

// Definir los iconos personalizados
const OllinIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props}>
    <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2M1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2" />
  </SvgIcon>
);

const RecoleccionIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props} viewBox="0 0 512 512">
    <path d="M184.561 261.903c3.232 13.997-12.123 24.635-24.068 17.168l-40.736-25.455-50.867 81.402C55.606 356.273 70.96 384 96.012 384H148c6.627 0 12 5.373 12 12v40c0 6.627-5.373 12-12 12H96.115c-75.334 0-121.302-83.048-81.408-146.88l50.822-81.388-40.725-25.448c-12.081-7.547-8.966-25.961 4.879-29.158l110.237-25.45c8.611-1.988 17.201 3.381 19.189 11.99l25.452 110.237zm98.561-182.915l41.289 66.076-40.74 25.457c-12.051 7.528-9 25.953 4.879 29.158l110.237 25.45c8.672 1.999 17.215-3.438 19.189-11.99l25.45-110.237c3.197-13.844-11.99-24.719-24.068-17.168l-40.687 25.424-41.263-66.082c-37.521-60.033-125.209-60.171-162.816 0l-17.963 28.766c-3.51 5.62-1.8 13.021 3.82 16.533l33.919 21.195c5.62 3.512 13.024 1.803 16.536-3.817l17.961-28.743c12.712-20.341 41.973-19.676 54.257-.022zM497.288 301.12l-27.515-44.065c-3.511-5.623-10.916-7.334-16.538-3.821l-33.861 21.159c-5.62 3.512-7.33 10.915-3.818 16.536l27.564 44.112c13.257 21.211-2.057 48.96-27.136 48.96H320V336.02c0-14.213-17.242-21.383-27.313-11.313l-80 79.981c-6.249 6.248-6.249 16.379 0 22.627l80 79.989C302.689 517.308 320 510.3 320 495.989V448h95.88c75.274 0 121.335-82.997 81.408-146.88z" />
  </SvgIcon>
);

const RouteGroupsCRUD: React.FC = () => {
  const [routeGroups, setRouteGroups] = useState<RouteGroup[]>([]);
  const [open, setOpen] = useState(false);
  const [currentRouteGroup, setCurrentRouteGroup] = useState<RouteGroup | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [currentRoute, setCurrentRoute] = useState<Route | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  // const [columnSelectionDialogOpen, setColumnSelectionDialogOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set([
    'name', 'routeType', 'dayOfWeek', 'weekOfMonth', 'createdAt', 'nextDelivery'
  ]));
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof RouteGroup>('name');
  const [tabValue, setTabValue] = useState(0);
  const [selectedRouteTypes, setSelectedRouteTypes] = useState<string[]>([]); // Añadir este estado
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [showNotificationDel, setShowNotificationDel] = useState(false);
  const [transportistas, setTransportistas] = useState<Transportista[]>([]);
  const [selectedTransportista, setSelectedTransportista] = useState<Transportista | null>(null);

  useEffect(() => {
    fetchRouteGroups();
    fetchClients();
    fetchInventoryItems();
  }, []);

  // Agregar después de los otros useEffect
  useEffect(() => {
    const fetchTransportistas = async () => {
      const transportistasRef = collection(db, 'transportistas');
      const snapshot = await getDocs(transportistasRef);
      const transportistasData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Transportista[];
      setTransportistas(transportistasData);
    };

    fetchTransportistas();
  }, []);

  const fetchRouteGroups = async () => {
    try {
      const q = query(collection(db, 'routeGroups'));
      const querySnapshot = await getDocs(q);
      const groups: RouteGroup[] = [];

      for (const docRef of querySnapshot.docs) {
        const groupData = docRef.data();
        const routesSnapshot = await getDocs(collection(db, 'routeGroups', docRef.id, 'routes'));
        const routes: Route[] = routesSnapshot.docs.map(routeDoc => ({
          id: routeDoc.id,
          ...routeDoc.data()
        } as Route));

        const routesUnionSnapshot = await getDocs(collection(db, 'routeGroups', docRef.id, 'routesUnion'));
        const routesUnion: [Route[], Date, boolean][] = routesUnionSnapshot.docs.map(routeDoc => {
          const data = routeDoc.data();
          return [
            [{ id: routeDoc.id, ...data.route } as Route],
            data.date?.toDate() || new Date(), // Add null check and default date
            data.isUnificationRoute || false // Add default boolean
          ] as [Route[], Date, boolean];
        });

        groups.push({
          id: docRef.id,
          name: groupData.name || '',
          dayOfWeek: groupData.dayOfWeek || 0,
          weekOfMonth: groupData.weekOfMonth || 0,
          createdAt: groupData.createdAt?.toDate() || new Date(), // Add null check
          routes: routes,
          routesTemp: groupData.routesTemp || [],
          routesUnion: routesUnion,
          routeType: groupData.routeType || 'monthly',
          unificationFlag: groupData.unificationFlag || false,
          month: groupData.month || 1
        });
      }

      setRouteGroups(groups);
    } catch (error) {
      console.error("Error fetching route groups: ", error);
      toast.error('Error fetching route groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const q = query(collection(db, 'clients'));
      const querySnapshot = await getDocs(q);
      setClients(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client)));
    } catch (error) {
      console.error("Error fetching clients: ", error);
      toast.error('Error fetching clients');
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const q = query(collection(db, 'inventoryItems'));
      const querySnapshot = await getDocs(q);
      setInventoryItems(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as InventoryItem)));
    } catch (error) {
      console.error("Error fetching inventory items: ", error);
      toast.error('Error fetching inventory items');
    }
  };

  const handleOpen = (routeGroup: RouteGroup | null = null) => {
    setCurrentRouteGroup(routeGroup || {
      name: '',
      dayOfWeek: 1,
      weekOfMonth: 1,
      createdAt: new Date(),
      routes: [],
      routesTemp: [],
      routesUnion: [],
      routeType: 'weekly',
      month: 1
    } as RouteGroup);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentRouteGroup(null);
  };

  const handleRouteTypeChange = (value: 'weekly' | 'biweekly' | 'monthly' | 'bimonthly') => {
    if (!currentRouteGroup) return;

    let updatedGroup: RouteGroup = {
      ...currentRouteGroup,
      routeType: value,
      dayOfWeek: value === 'monthly' || value === 'bimonthly' ? (currentRouteGroup.dayOfWeek || 1) : 0,
      weekOfMonth: value === 'monthly' || value === 'bimonthly' ? (currentRouteGroup.weekOfMonth || 1) : 0,
      createdAt: value === 'monthly' || value === 'bimonthly' ? new Date() : (currentRouteGroup.createdAt || new Date()),
      month: value === 'monthly' || value === 'bimonthly' ? (currentRouteGroup.month || 1) : 0,
    };
    setCurrentRouteGroup(updatedGroup);
  };

  const handleCreatedAtChange = (date: Date | null) => {
    if (!currentRouteGroup || !date) return;
    setCurrentRouteGroup({
      ...currentRouteGroup,
      createdAt: date
    });
  };


  const updateInventory = async (newItems: OrderItem[], oldItems: OrderItem[] = []) => {
    const batch = writeBatch(db);

    const oldQuantities = new Map(oldItems.map(item => [item.inventoryItemId, item.quantity]));

    for (const item of newItems) {
      const itemRef = doc(db, 'inventoryItems', item.inventoryItemId);
      const itemDoc = await getDoc(itemRef);

      if (!itemDoc.exists()) {
        console.warn(`El item con ID ${item.inventoryItemId} no existe en el inventario.`);
        continue;
      }

      const oldQuantity = oldQuantities.get(item.inventoryItemId) || 0;
      const quantityDifference = oldQuantity - item.quantity;
      batch.update(itemRef, { quantity: increment(quantityDifference) });
    }

    for (const oldItem of oldItems) {
      if (!newItems.some(newItem => newItem.inventoryItemId === oldItem.inventoryItemId)) {
        const itemRef = doc(db, 'inventoryItems', oldItem.inventoryItemId);
        const itemDoc = await getDoc(itemRef);

        if (!itemDoc.exists()) {
          console.warn(`El item con ID ${oldItem.inventoryItemId} no existe en el inventario.`);
          continue;
        }

        batch.update(itemRef, { quantity: increment(oldItem.quantity) });
      }
    }

    await batch.commit();
  };

  const handleSave = async () => {
    try {
      setShowNotification(true);
      if (!currentRouteGroup || !isFormValid()) return;

      const batch = writeBatch(db);
      const isNewGroup = !currentRouteGroup.id;

      // Handle existing or new route group
      const routeGroupRef = isNewGroup
        ? doc(collection(db, 'routeGroups'))
        : doc(db, 'routeGroups', currentRouteGroup.id as string);

      // Common route group data
      const routeGroupData = {
        id: routeGroupRef.id, // Usar el ID generado por Firestore
        name: currentRouteGroup.name,
        routeType: currentRouteGroup.routeType,
        dayOfWeek: currentRouteGroup.dayOfWeek,
        weekOfMonth: currentRouteGroup.weekOfMonth,
        createdAt: Timestamp.fromDate(currentRouteGroup.createdAt || new Date()),
        month: currentRouteGroup.month
      };

      if (isNewGroup) {
        // Link transportista if selected
        if (selectedTransportista?.id) {
          const transportistaRef = doc(db, 'transportistas', selectedTransportista.id);
          await updateDoc(transportistaRef, {
            listaRutas: arrayUnion(routeGroupRef.id)
          });
        }

        batch.set(routeGroupRef, routeGroupData);
      } else {
        // Update existing route group
        const { routesUnion, ...routeGroupDataForUpdate } = currentRouteGroup;
        await updateDoc(routeGroupRef, {
          ...routeGroupDataForUpdate,
          updatedAt: new Date()
        });
        batch.update(routeGroupRef, routeGroupData);
      }

      // Handle routes
      const routesRef = collection(routeGroupRef, 'routes');

      if (!isNewGroup) {
        // Delete existing routes
        const existingRoutes = await getDocs(routesRef);
        existingRoutes.forEach(doc => batch.delete(doc.ref));
      }

      // Add new routes
      currentRouteGroup.routes.forEach(route => {
        const newRouteRef = doc(routesRef);
        batch.set(newRouteRef, {
          id: newRouteRef.id,
          clientId: route.clientId,
          status: route.status,
          order: route.order
        });
      });

      await batch.commit();

      // Update Inventory Quantities
      // const allOrderItems = currentRouteGroup.routes.map(route => route.order?.items || []).flat();
      // await updateInventory(allOrderItems, []);

      // // Update inventory items locally
      // const updatedInventoryItems = inventoryItems.map(item => {
      //   const orderItem = allOrderItems.find(oi => oi.inventoryItemId === item.id);
      //   if (orderItem) {
      //     return { ...item, quantity: item.quantity - orderItem.quantity };
      //   }
      //   return item;
      // });
      // setInventoryItems(updatedInventoryItems);

      toast.success(isNewGroup
        ? 'Nuevo grupo de rutas creado exitosamente'
        : 'Grupo de rutas actualizado exitosamente'
      );

      handleClose();
      fetchRouteGroups();
      window.location.reload();
    } catch (error) {
      console.error("Error saving route group: ", error);
      toast.error('Error al guardar el grupo de rutas');
    } finally {
      setShowNotification(false);
    }
  };

  const handleDelete = async (id: string) => {
    setGroupToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setShowNotificationDel(true);
    if (!groupToDelete) return;

    try {
      // Create batch operation
      const batch = writeBatch(db);

      // Get reference to RouteGroup document
      const routeGroupRef = doc(db, 'routeGroups', groupToDelete);

      // Get reference to routes subcollection
      const routesRef = collection(routeGroupRef, 'routes');

      // Get reference to routesUnion subcollection
      const routesUnionRef = collection(routeGroupRef, 'routesUnion');

      // Get all routes in the group
      const routesSnapshot = await getDocs(routesRef);

      // Get all routesUnion documents
      const routesUnionSnapshot = await getDocs(routesUnionRef);

      // Find and delete matching orders
      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(ordersRef, where('routeGroupId', '==', groupToDelete));
      const ordersSnapshot = await getDocs(ordersQuery);

      // Delete matching orders in batch
      ordersSnapshot.docs.forEach((orderDoc) => {
        batch.delete(orderDoc.ref);
      });

      // Delete all routes in batch
      routesSnapshot.docs.forEach((routeDoc) => {
        batch.delete(routeDoc.ref);
      });

      // Delete all routesUnion documents in batch
      routesUnionSnapshot.docs.forEach((routeUnionDoc) => {
        batch.delete(routeUnionDoc.ref);
      });

      // Remove RouteGroup ID from Transportista's listaRutas
      const transportistasRef = collection(db, 'transportistas');
      const transportistasSnapshot = await getDocs(transportistasRef);

      for (const transportistaDoc of transportistasSnapshot.docs) {
        const transportistaData = transportistaDoc.data();
        const listaRutas = transportistaData.listaRutas as string[] | undefined;

        if (listaRutas && listaRutas.includes(groupToDelete)) {
          // Remove the RouteGroup ID from the listaRutas array
          const updatedListaRutas = listaRutas.filter(routeId => routeId !== groupToDelete);
          const transportistaRef = doc(db, 'transportistas', transportistaDoc.id);
          batch.update(transportistaRef, { listaRutas: updatedListaRutas });
          break; // Exit loop after finding and updating the transportista
        }
      }

      // Delete the main RouteGroup document
      batch.delete(routeGroupRef);

      // Execute batch operation
      await batch.commit();

      // Refresh data and show success message
      await fetchRouteGroups();

      window.location.reload();

      toast.success('Grupo de rutas y órdenes asociadas eliminados exitosamente');

    } catch (error) {
      console.error("Error al eliminar el grupo de rutas: ", error);
      toast.error('Error al eliminar el grupo de rutas');
    } finally {
      // Reset state
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      setShowNotificationDel(false);
    }
  };

  const removeRoute = (index: number) => {
    if (!currentRouteGroup) return;
    const updatedRoutes = currentRouteGroup.routes.filter((_, i) => i !== index);
    setCurrentRouteGroup({ ...currentRouteGroup, routes: updatedRoutes });
    toast.info('Ruta eliminada');
  };

  const handleRouteChange = (index: number, field: keyof Route, value: any) => {
    if (!currentRouteGroup) return;
    const updatedRoutes = [...currentRouteGroup.routes];
    updatedRoutes[index] = { ...updatedRoutes[index], [field]: value };
    setCurrentRouteGroup({ ...currentRouteGroup, routes: updatedRoutes });
  };

  const addRoute = () => {
    if (!currentRouteGroup) return;
    const newRoute: Route = {
      clientId: '',
      status: 'pending',
      order: { items: [] },
      tipo_pedido: 'Automatizado',
      fecha_pedido: new Date(),
      routeGroupId: currentRouteGroup.id || ''
    };
    setCurrentRouteGroup({
      ...currentRouteGroup,
      routes: [...currentRouteGroup.routes, newRoute]
    });
  };

  const handleCreateOrEditOrder = (routeGroup: RouteGroup, route: Route) => {
    setCurrentRouteGroup(routeGroup);
    setCurrentRoute(route);
    setIsOrderModalOpen(true);
  };

  const handleOrderCreated = async (order: Order) => {
    if (currentRouteGroup && currentRoute) {
      try {
        const updatedRoute: Route = {
          ...currentRoute,
          order: order,
          status: 'pending'
        };

        const updatedRoutes = currentRouteGroup.routes.map(r =>
          r === currentRoute ? updatedRoute : r
        );

        setCurrentRouteGroup({
          ...currentRouteGroup,
          routes: updatedRoutes
        });

        // const updatedInventoryItems = [...inventoryItems];

        // // Create a map of item IDs to quantities in the new order
        // const newOrderQuantities = new Map(order.items.map(item => [item.inventoryItemId, item.quantity]));

        // // Create a map of item IDs to quantities in the existing order (if any)
        // const existingOrderQuantities = new Map(
        //   (currentRoute.order?.items || []).map(item => [item.inventoryItemId, item.quantity])
        // );

        // // Iterate through all inventory items and update quantities based on the difference between the new and existing orders
        // updatedInventoryItems.forEach(item => {
        //   const newItemQuantity = newOrderQuantities.get(item.id) || 0;
        //   const existingItemQuantity = existingOrderQuantities.get(item.id) || 0;

        //   // Calculate the quantity difference
        //   const quantityDifference = existingItemQuantity - newItemQuantity;

        //   // Apply the quantity difference to the inventory item
        //   item.quantity += quantityDifference;
        // });

        // setInventoryItems(updatedInventoryItems);

        setIsOrderModalOpen(false);
        setCurrentRoute(null);
        toast.success('Orden creada exitosamente');
      } catch (error) {
        console.error("Error updating route group with new order: ", error);
        toast.error('Error al actualizar el grupo de rutas con la nueva orden');
      }
    }
  };

  const isFormValid = () => {
    if (!currentRouteGroup) return false;
    return (
      currentRouteGroup.name.trim() !== '' &&
      currentRouteGroup.routes.length > 0 &&
      currentRouteGroup.routes.every(route =>
        route.clientId && (
          (route.order && route.order.items && route.order.items.some(item => item.quantity > 0)) ||
          route.status === 'pending'
        )
      )
    );
  };

  const getDayOfWeekName = (day: number = 0) => {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[day];
  };

  const calculateNextDeliveryDate = (group: RouteGroup): Date => {
    const today = new Date();
    let nextDelivery = new Date(group.createdAt);

    switch (group.routeType) {
      case 'weekly':
        while (nextDelivery <= today) {
          nextDelivery.setDate(nextDelivery.getDate() + 7);
        }
        break;
      case 'biweekly':
        while (nextDelivery <= today) {
          nextDelivery.setDate(nextDelivery.getDate() + 14);
        }
        break;
      case 'monthly':
      case 'bimonthly':
        while (nextDelivery <= today) {
          nextDelivery.setMonth(nextDelivery.getMonth() + (group.routeType === 'monthly' ? 1 : 2));
          nextDelivery.setDate(1);
          while (nextDelivery.getDay() !== group.dayOfWeek) {
            nextDelivery.setDate(nextDelivery.getDate() + 1);
          }
          nextDelivery.setDate(nextDelivery.getDate() + ((group.weekOfMonth ?? 1) - 1) * 7);
        }
        break;
    }

    return nextDelivery;
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterDialogOpen = () => {
    setFilterDialogOpen(true);
  };

  const handleFilterDialogClose = () => {
    setFilterDialogOpen(false);
  };

  // const handleColumnSelectionDialogOpen = () => {
  //   setColumnSelectionDialogOpen(true);
  // };

  // const handleColumnSelectionDialogClose = () => {
  //   setColumnSelectionDialogOpen(false);
  // };

  const handleColumnToggle = (column: string) => {
    setVisibleColumns(prevColumns => {
      const newColumns = new Set(prevColumns);
      if (newColumns.has(column)) {
        newColumns.delete(column);
      } else {
        newColumns.add(column);
      }
      return newColumns;
    });
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value
    }));
    // Actualiza las selecciones de tipo de ruta
    if (field === 'routeType') {
      setSelectedRouteTypes(value);
    }
  };

  const applyFilters = () => {
    // Apply filters logic here
    handleFilterDialogClose();
  };

  const normalizeString = (str: string): string => {
    return str
      .normalize('NFD') // Normalize to NFD Unicode normalization form (separates base characters from diacritics)
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .toLowerCase();
  };

  const filteredRouteGroups = useMemo(() => {
    const sortedGroups = [...routeGroups].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Manejo de la columna "Siguiente Entrega"
      if (orderBy === 'nextDelivery' as keyof RouteGroup) {
        aValue = calculateNextDeliveryDate(a).getTime(); // Convertir a timestamp
        bValue = calculateNextDeliveryDate(b).getTime(); // Convertir a timestamp
      } else {
        aValue = a[orderBy as keyof RouteGroup];
        bValue = b[orderBy as keyof RouteGroup];
      }

      if (aValue === undefined || bValue === undefined) {
        return 0; // Si alguno es undefined, no se cambia el orden
      }

      if (order === 'desc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0; // Comparación para orden ascendente
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0; // Comparación para orden descendente
      }
    });

    if (!searchTerm) {
      return sortedGroups.filter(group => group.unificationFlag === undefined || group.unificationFlag === false);
    }

    const normalizedSearch = normalizeString(searchTerm);
    
    return sortedGroups.filter(group => {
      const matchesRouteType = selectedRouteTypes.length === 0 || selectedRouteTypes.includes(group.routeType || '');
      
      // Normalize all fields for comparison
      const normalizedName = group.name ? normalizeString(group.name) : '';
      const normalizedRouteType = group.routeType ? normalizeString(group.routeType) : '';
      const normalizedDayOfWeek = group.dayOfWeek ? normalizeString(getDayOfWeekName(group.dayOfWeek)) : '';
      const normalizedWeekOfMonth = group.weekOfMonth ? normalizeString(group.weekOfMonth.toString()) : '';
      const normalizedCreatedAt = group.createdAt ? normalizeString(group.createdAt.toLocaleDateString()) : '';
      
      const matchesSearchTerm = (
        normalizedName.includes(normalizedSearch) ||
        normalizedRouteType.includes(normalizedSearch) ||
        normalizedDayOfWeek.includes(normalizedSearch) ||
        normalizedWeekOfMonth.includes(normalizedSearch) ||
        normalizedCreatedAt.includes(normalizedSearch) ||
        normalizeString(calculateNextDeliveryDate(group).toLocaleDateString()).includes(normalizedSearch)
      );
      
      const hasUnificationFlagUndefinedOrFalse = group.unificationFlag === undefined || group.unificationFlag === false;
      return matchesRouteType && matchesSearchTerm && hasUnificationFlagUndefinedOrFalse;
    });
  }, [routeGroups, selectedRouteTypes, searchTerm, order, orderBy]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadCSV = () => {
    const headers = Array.from(visibleColumns);
    const csvContent = [
      headers.join(','),
      ...filteredRouteGroups.map(group =>
        headers.map(header => {
          if (header === 'nextDelivery') {
            return calculateNextDeliveryDate(group).toLocaleDateString();
          }
          return (group[header as keyof RouteGroup] || '').toString();
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'route_groups.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  const handleRequestSort = (property: keyof RouteGroup) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'contents', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            color: 'rgb(0, 180, 216)'
          }}>
            <h1>Automatización de Rutas</h1>
          </Box>
          <div style={{
            width: '100%',
            height: '4px',
            backgroundColor: 'rgb(0, 180, 216)',
            margin: '20px 0'
          }} />
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            onClick={() => handleOpen()}
            sx={{
              mb: 3, backgroundColor: '#00b4d8',
              '&:hover': {
                backgroundColor: 'rgb(0, 180, 216)',
              },
              fontWeight: 'bold'
            }}
          >
            Agregar Ruta
          </Button>
          {/* <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Tabla de Rutas" />
            <Tab label="Lista de Cartas" />
          </Tabs> */}
          {/* <FormControlLabel
            control={
              <Switch
                checked={tabValue === 1}
                onChange={() => setTabValue(tabValue === 0 ? 1 : 0)}
                color="primary"
                sx={{
                  '&.MuiSwitch-root': {
                    '& .MuiSwitch-thumb': {
                      backgroundColor: 'rgb(0, 180, 216) !important',
                    }
                  },
                }}
              />
            }
            label={
              <span style={{ fontWeight: 'bold' }}>
                {tabValue === 0 ? "Tabla de Rutas" : "Lista de Cartas"}
              </span>
            }
            sx={{ mb: 2, marginBottom: '21px' }}
          /> */}
        </Box>

        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
            sx={{ alignItems: 'left' }}
          />

          <Box sx={{ alignItems: 'right' }}>
            <Tooltip title="Filtrar">
              <IconButton onClick={handleFilterDialogOpen}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Seleccionar columnas">
              <IconButton onClick={handleColumnSelectionDialogOpen}>
                <ViewColumnIcon />
              </IconButton>
            </Tooltip> */}
            <Tooltip title="Imprimir">
              <IconButton onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Descargar CSV">
              <IconButton onClick={handleDownloadCSV}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      {/* Mostrar filtros seleccionados */}
      <Box sx={{ mb: 2, marginTop: '13px' }}>
        {selectedRouteTypes.map((type) => (
          <Chip
            key={type}
            label={type === 'weekly' ? 'Semanal' :
              type === 'biweekly' ? 'Bisemanal' :
                type === 'monthly' ? 'Mensual' : 'Bimensual'}
            onDelete={() => {
              const newSelected = selectedRouteTypes.filter(t => t !== type);
              setSelectedRouteTypes(newSelected);
              handleFilterChange('routeType', newSelected); // Actualiza los filtros
            }}
            sx={{ mr: 1, backgroundColor: 'rgb(0, 180, 216)', color: 'white' }}
          />
        ))}
      </Box>

      {
        tabValue === 0 && (
          // Vista de Tabla
          <Box>
            {/* Barra de búsqueda para la tabla */}
            <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, minHeight: 'calc(60vh)' }}>
              <Table sx={{ minWidth: 650 }} aria-label="route groups table">
                <TableHead sx={{ backgroundColor: 'rgb(0, 180, 216)' }}>
                  <TableRow>
                    {Array.from(visibleColumns).map((column) => (
                      <TableCell key={column}>
                        <TableSortLabel
                          active={orderBy === column}
                          direction={orderBy === column ? order : 'asc'}
                          onClick={() => handleRequestSort(column as keyof RouteGroup)}
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            '&.Mui-active': {
                              color: 'white',
                            },
                            '&:hover': {
                              color: '#e3f2fd',
                            },
                            '& .MuiTableSortLabel-icon': {
                              color: 'inherit !important'
                            }
                          }}
                        >
                          {columnNames[column as keyof typeof columnNames]}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ minHeight: '400px' }}>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumns.size + 1}
                        sx={{
                          height: '470px',
                          border: 'none'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            height: '100%',
                            width: '100%',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <CircularProgress size={60} /> {/* Increased size for better visibility */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : filteredRouteGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center' }}>
                        <Typography>
                          {searchTerm.trim()
                            ? 'No se encontraron resultados para la búsqueda'
                            : 'No hay rutas automatizadas para mostrar, animate a create una!'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {filteredRouteGroups
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((group) => {
                          const nextDelivery = calculateNextDeliveryDate(group);
                          return (
                            <TableRow
                              key={group.id}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.1)',
                                  '& .edit-icon': {
                                    opacity: 1,
                                  },
                                },
                              }}
                            >
                              {visibleColumns.has('name') && <TableCell>{group.name}</TableCell>}
                              {visibleColumns.has('routeType') && (
                                <TableCell>
                                  {group.routeType === 'weekly' ? 'Semanal' :
                                    group.routeType === 'biweekly' ? 'Bisemanal' :
                                      group.routeType === 'monthly' ? 'Mensual' : 'Bimensual'}
                                </TableCell>
                              )}
                              {visibleColumns.has('dayOfWeek') && (
                                <TableCell>
                                  {(group.routeType === 'monthly' || group.routeType === 'bimonthly') &&
                                    getDayOfWeekName(group.dayOfWeek)}
                                </TableCell>
                              )}
                              {visibleColumns.has('weekOfMonth') && (
                                <TableCell>
                                  {(group.routeType === 'monthly' || group.routeType === 'bimonthly') &&
                                    group.weekOfMonth}
                                </TableCell>
                              )}
                              {visibleColumns.has('createdAt') && (
                                <TableCell>
                                  {(group.routeType === 'weekly' || group.routeType === 'biweekly') &&
                                    group.createdAt.toLocaleDateString()}
                                </TableCell>
                              )}
                              {visibleColumns.has('nextDelivery') && <TableCell>{nextDelivery.toLocaleDateString()}</TableCell>}
                              <TableCell>
                                <IconButton
                                  onClick={() => handleOpen(group)}
                                  sx={{
                                    color: '#00b4d8',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 180, 216, 0.1)',
                                    },
                                  }}
                                >
                                  <EditIcon className="edit-icon" sx={{ color: '#00b4d8', marginRight: '8px' }} />
                                  <Typography variant="button" sx={{ color: '#00b4d8' }}></Typography>
                                </IconButton>
                                <IconButton color="error" onClick={() => group.id && handleDelete(group.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[7, 11, 25]}
              component="div"
              count={filteredRouteGroups.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
                '.MuiTablePagination-toolbar': {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Box>
        )
      }

      {
        tabValue === 1 && (
          // Vista de Lista de Cartas
          <Grid container spacing={3}>
            {filteredRouteGroups.map((group) => (
              <Grid item xs={12} sm={6} md={4} key={group.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent>
                    <Typography variant="h6" component="div" gutterBottom>
                      {group.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tipo de Ruta: {group.routeType === 'weekly' ? 'Semanal' :
                        group.routeType === 'biweekly' ? 'Bisemanal' :
                          group.routeType === 'monthly' ? 'Mensual' : 'Bimensual'}
                    </Typography>
                    {(group.routeType === 'monthly' || group.routeType === 'bimonthly') && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Día de la Semana: {getDayOfWeekName(group.dayOfWeek)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Semana del Mes: {group.weekOfMonth}
                        </Typography>
                      </>
                    )}
                    {(group.routeType === 'weekly' || group.routeType === 'biweekly') && (
                      <Typography variant="body2" color="text.secondary">
                        Fecha de Inicio: {group.createdAt.toLocaleDateString()}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Siguiente Entrega: {calculateNextDeliveryDate(group).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => handleOpen(group)}>Editar</Button>
                    <Button size="small" color="error" onClick={() => group.id && handleDelete(group.id)}>Eliminar</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      }

      <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#87CEEB', color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}> {/* Añadido para espaciar los elementos */}
            <Avatar sx={{
              bgcolor: '#FFA500',
              color: 'white',
              width: 47,
              height: 47,
              fontSize: 27
            }}>
              {currentRouteGroup?.routes.length} {/* Aquí puedes usar el total de pedidos que desees mostrar */}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', ml: 1 }}>
              Total de Pedidos
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 0.7, textAlign: 'center', ml: '3em' }}> {/* Añadido para centrar el texto */}
            {currentRouteGroup?.id ? 'Editar la Automatización' : 'Crear Ruta Automatizada'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* <Tabs
            value={0}
            aria-label="route group tabs"
            sx={{
              mb: 2,
              bgcolor: 'background.paper', // Fondo de las pestañas
              borderRadius: 2, // Bordes redondeados
              boxShadow: 1, // Sombra para un efecto elevado
              '& .MuiTab-root': {
                color: '#87CEEB', // Color de texto por defecto
                '&.Mui-selected': {
                  color: 'white', // Color de texto cuando está seleccionado
                  backgroundColor: '#5CACEE', // Fondo cuando está seleccionado
                  borderRadius: 2, // Bordes redondeados para la pestaña seleccionada
                },
                '&:hover': {
                  backgroundColor: 'rgba(92, 202, 238, 0.2)', // Efecto hover
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#5CACEE', // Color del indicador
              },
            }}
          >
            <Tab label="Configuración de la Automatización" />
          </Tabs> */}
          <Grid container spacing={3}>
            <Grid item xs={9}>
              <Box sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <SectionTitle variant="h6">
                    <IconWrapper>
                      <FormatListBulleted />
                      <Typography variant="h6" sx={{ ml: '1rem' }}>
                        Lista de Pedidos
                      </Typography>
                    </IconWrapper>
                  </SectionTitle>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addRoute}
                    variant="contained"
                    sx={{
                      mt: 2,
                      backgroundColor: '#00b4d8',
                      '&:hover': {
                        backgroundColor: '#0077be',
                      },
                    }}
                  >
                    Agregar Pedido
                  </Button>
                </Box>

                {currentRouteGroup?.routes.map((route, index) => (
                  <Paper
                    key={route.id || `route-${index}`}
                    elevation={1}
                    sx={{
                      mb: 2,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'white',
                      borderLeft: `6px solid ${index === 0 ? '#FFA500' : '#00b4d8'}`,
                      transition: 'all 0.3s ease',
                      '&:hover': { transform: 'translateY(-4px)' }
                    }}
                  >
                    <Avatar sx={{
                      bgcolor: (index === 0 || index === currentRouteGroup.routes.length - 1) ? '#FFA500' : '#00b4d8',
                      color: 'white',
                      mr: 2
                    }}>
                      {index + 1}
                    </Avatar>

                    <Tooltip title={`Tipo de entrega: ${clients.find(c => c.id === route.clientId)?.tipo_entrega || 'No especificado'}`}>
                      <IconButton sx={{ mr: 2 }}>
                        {clients.find(c => c.id === route.clientId)?.tipo_entrega === 'Recolección'
                          ? <RecoleccionIcon sx={{ color: 'green' }} />
                          : <OllinIcon sx={{ color: '#00b4d8' }} />}
                      </IconButton>
                    </Tooltip>

                    <FormControl fullWidth sx={{ mr: 2, width: '400px', height: '56px' }}>
                      <Autocomplete
                        options={clients}
                        value={clients.find(client => client.id === route.clientId) || null}
                        onChange={(e, newValue) => {
                          handleRouteChange(index, 'clientId', newValue?.id || '');
                        }}
                        filterOptions={(options, state) => {
                          const inputValue = state.inputValue.toLowerCase();
                          const selectedValue = clients.find(client => client.id === route.clientId)?.name.toLowerCase() || '';
                          const searchText = state.inputValue ? inputValue : selectedValue;

                          return options.filter(client =>
                            (!currentRouteGroup.routes.some(r => r.clientId === client.id && r !== route)) &&
                            client.name.toLowerCase().includes(searchText)
                          );
                        }}
                        getOptionLabel={(option) => option.name || ''}
                        popupIcon={null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Cliente"
                            variant="outlined"
                            InputLabelProps={{
                              sx: { backgroundColor: 'white', padding: '0 4px', fontSize: '1rem', fontWeight: 'bold' }
                            }}
                          />
                        )}
                        noOptionsText="No se encontraron clientes"
                      />
                    </FormControl>
                    {route.clientId && (
                      (() => {
                        const selectedClient = clients.find(client => client.id === route.clientId);
                        if (!selectedClient) return null; // Asegúrate de que selectedClient esté definido

                        return (
                          <Box sx={{ mb: 1, mt: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <FormControl fullWidth sx={{ flex: 1, height: '56px' }}>
                              <InputLabel
                                sx={{
                                  backgroundColor: 'white',
                                  padding: '0 4px',
                                  fontSize: '1rem',
                                  fontWeight: 'bold',
                                }}
                                shrink={!!selectedClient.canton} // Shrink if there is a value
                              >
                                Cantón
                              </InputLabel>

                              <TextField
                                value={selectedClient.canton}
                                InputProps={{
                                  readOnly: true, // Make it read-only if you don't want to allow editing
                                  sx: {
                                    borderRadius: '8px', // Aplicar borderRadius aquí
                                  },
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                  fontSize: '0.9rem',
                                  backgroundColor: '#f9f9f9',
                                  height: '100%', // Asegurarse de que el TextField ocupe toda la altura
                                }}
                              />
                            </FormControl>
                            <FormControl fullWidth sx={{ flex: 1, height: '56px' }}>
                              <InputLabel
                                sx={{
                                  backgroundColor: 'white',
                                  padding: '0 4px',
                                  fontSize: '1rem',
                                  fontWeight: 'bold',
                                }}
                                shrink={!!selectedClient.distrito} // Shrink if there is a value
                              >
                                Distrito
                              </InputLabel>
                              <TextField
                                value={selectedClient.distrito}
                                InputProps={{
                                  readOnly: true, // Make it read-only if you don't want to allow editing
                                  sx: {
                                    borderRadius: '8px', // Aplicar borderRadius aquí
                                  },
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00b4d8' },
                                  fontSize: '0.9rem',
                                  backgroundColor: '#f9f9f9',
                                  height: '100%', // Asegurarse de que el TextField ocupe toda la altura
                                }}
                              />
                            </FormControl>
                          </Box>
                        );
                      })()
                    )}
                    <IconButton
                      onClick={() => handleCreateOrEditOrder(currentRouteGroup!, route)}
                      sx={{
                        mr: 1, // Reducido para acercar al botón de eliminar
                        border: 'none', // Quitar el borde
                        color: '#00b4d8',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 180, 216, 0.1)',
                        },
                      }}
                    >
                      {route.order && route.order.items && route.order.items.length > 0 ? (
                        <EditIcon /> // Icono para editar
                      ) : (
                        <AddIcon /> // Icono para crear
                      )}
                    </IconButton>
                    <IconButton onClick={() => removeRoute(index)} sx={{ color: '#F44336' }}>
                      <DeleteIcon />
                    </IconButton>
                  </Paper>
                ))}

              </Box>
            </Grid>
            <Grid item xs={3}>
              <InfoSection elevation={3}>
                <SectionTitle variant="h6" sx={{ ml: '2rem' }}>
                  <IconWrapper>
                    <CalendarToday />
                    <Typography variant="h6" sx={{ ml: '2rem' }}>
                      Configuración de la Automatización
                    </Typography>
                  </IconWrapper>
                </SectionTitle>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <StyledFormControl fullWidth sx={{ backgroundColor: 'white' }}>
                      <TextField
                        label="Nombre"
                        value={currentRouteGroup?.name || ''}
                        onChange={(e) => setCurrentRouteGroup(currentRouteGroup ? { ...currentRouteGroup, name: e.target.value } : null)}
                        placeholder="Ingrese el nombre de la ruta"
                        InputLabelProps={{
                          sx: {
                            '&.MuiInputLabel-shrink': {
                              backgroundColor: 'white',
                              padding: '0 8px',
                            },
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            color: '#00b4d8',
                            transition: 'all 0.2s ease',
                            '&:hover fieldset': {
                              borderColor: '#0077be',
                              borderWidth: '2px'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#00b4d8',
                              borderWidth: '2px'
                            },
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <DriveFileRenameOutline
                              sx={{
                                color: '#00b4d8',
                                mr: 1,
                                transition: 'all 0.2s ease',
                                transform: 'scale(0.9)'
                              }}
                            />
                          ),
                          sx: {
                            borderRadius: '8px',
                            transition: 'all 0.2s ease'
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#00b4d8',
                              borderWidth: '2px',
                              transition: 'all 0.2s ease'
                            },
                            '&:hover fieldset': {
                              borderColor: '#0077be'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#00b4d8'
                            },
                            '& legend': {
                              width: 0, // This helps with the label background
                            }
                          },
                          '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                            backgroundColor: 'white',
                            padding: '0 8px',
                          },
                          '& .MuiInputBase-input': {
                            color: '#2c3e50',
                            fontWeight: 500,
                            padding: '14px'
                          },
                          '& .MuiFormHelperText-root': {
                            margin: '4px 0 0'
                          }
                        }}
                      />
                    </StyledFormControl>
                  </Grid>
                  {(!currentRouteGroup?.id) && (
                    <>
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <InputLabel
                            sx={{
                              backgroundColor: 'white',
                              padding: '0 4px',
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              color: '#00b4d8',
                              transition: 'all 0.2s ease',
                              transform: 'translate(14px, -6px) scale(0.75)', // Adjusted position
                              '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -6px) scale(0.75)',
                              }
                            }}
                          >
                            Tipo de Ruta
                          </InputLabel>
                          <Select
                            value={currentRouteGroup?.routeType || 'weekly'}
                            onChange={(e) => handleRouteTypeChange(e.target.value as 'weekly' | 'biweekly' | 'monthly' | 'bimonthly')}
                            sx={{
                              height: '60px', // Fixed height
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8',
                                borderWidth: '2px',
                                borderRadius: '8px',
                                transition: 'all 0.2s ease'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0077be',
                                borderWidth: '2px'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8',
                                borderWidth: '2px'
                              },
                              backgroundColor: 'white',
                              '& .MuiSelect-select': {
                                padding: '10px',
                                color: '#2c3e50',
                                fontWeight: 500,
                                height: '60px', // Match parent height
                                display: 'flex',
                                alignItems: 'center'
                              }
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  marginTop: '4px', // Add some space between select and menu
                                  backgroundColor: 'white',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                  '& .MuiMenuItem-root': {
                                    minHeight: '48px', // Minimum height for menu items
                                    padding: '12px 16px',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 180, 216, 0.08)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 2px 8px rgba(0, 180, 216, 0.15)'
                                    },
                                    '&.Mui-selected': {
                                      backgroundColor: 'rgba(0, 180, 216, 0.12)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 180, 216, 0.16)'
                                      }
                                    }
                                  }
                                }
                              }
                            }}
                          >
                            <MenuItem
                              value="weekly"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                margin: '4px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.08)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 2px 8px rgba(0, 180, 216, 0.15)'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.12)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.16)'
                                  }
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                  sx={{
                                    backgroundColor: 'rgba(0, 180, 216, 0.1)',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <CalendarViewWeek
                                    sx={{
                                      color: '#00b4d8',
                                      fontSize: '1.5rem',
                                      transition: 'all 0.2s ease'
                                    }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      color: '#2c3e50',
                                      // fontSize: '0.95rem',
                                      // transition: 'color 0.2s ease', // Añadida transición para el texto
                                      // '&:hover': {
                                      //   color: '#00b4d8' // Cambio de color del texto en hover
                                      // }
                                    }}
                                  >
                                    Semanal
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    Entregas cada semana
                                  </Typography>
                                </Box>
                              </Box>
                              <NavigateNext
                                sx={{
                                  color: '#94a3b8',
                                  opacity: 0,
                                  transition: 'all 0.2s ease',
                                  transform: 'translateX(-8px)',
                                  '.MuiMenuItem-root:hover &': {
                                    opacity: 1,
                                    transform: 'translateX(0)',
                                    color: '#00b4d8'
                                  }
                                }}
                              />
                            </MenuItem>

                            <MenuItem
                              value="biweekly"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                margin: '4px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.08)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 2px 8px rgba(0, 180, 216, 0.15)'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.12)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.16)'
                                  }
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                  sx={{
                                    backgroundColor: 'rgba(0, 180, 216, 0.1)',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <CalendarToday
                                    sx={{
                                      color: '#00b4d8',
                                      fontSize: '1.5rem',
                                      transition: 'all 0.2s ease'
                                    }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.95rem' }}>
                                    Bisemanal
                                  </Typography>
                                  <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                    Entregas cada dos semanas
                                  </Typography>
                                </Box>
                              </Box>
                              <NavigateNext
                                sx={{
                                  color: '#94a3b8',
                                  opacity: 0,
                                  transition: 'all 0.2s ease',
                                  transform: 'translateX(-8px)',
                                  '.MuiMenuItem-root:hover &': {
                                    opacity: 1,
                                    transform: 'translateX(0)',
                                    color: '#00b4d8'
                                  }
                                }}
                              />
                            </MenuItem>

                            <MenuItem
                              value="monthly"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                margin: '4px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.08)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 2px 8px rgba(0, 180, 216, 0.15)'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.12)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.16)'
                                  }
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                  sx={{
                                    backgroundColor: 'rgba(0, 180, 216, 0.1)',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <CalendarViewMonth
                                    sx={{
                                      color: '#00b4d8',
                                      fontSize: '1.5rem',
                                      transition: 'all 0.2s ease'
                                    }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      color: '#2c3e50',
                                      fontSize: '0.95rem'
                                    }}
                                  >
                                    Mensual
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    Entregas una vez al mes
                                  </Typography>
                                </Box>
                              </Box>
                              <NavigateNext
                                sx={{
                                  color: '#94a3b8',
                                  opacity: 0,
                                  transition: 'all 0.2s ease',
                                  transform: 'translateX(-8px)',
                                  '.MuiMenuItem-root:hover &': {
                                    opacity: 1,
                                    transform: 'translateX(0)',
                                    color: '#00b4d8'
                                  }
                                }}
                              />
                            </MenuItem>

                            <MenuItem
                              value="bimonthly"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                margin: '4px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.08)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 2px 8px rgba(0, 180, 216, 0.15)'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.12)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.16)'
                                  }
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                  sx={{
                                    backgroundColor: 'rgba(0, 180, 216, 0.1)',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <CalendarMonth
                                    sx={{
                                      color: '#00b4d8',
                                      fontSize: '1.5rem',
                                      transition: 'all 0.2s ease'
                                    }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      color: '#2c3e50',
                                      fontSize: '0.95rem'
                                    }}
                                  >
                                    Bimensual
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    Entregas cada dos meses
                                  </Typography>
                                </Box>
                              </Box>
                              <NavigateNext
                                sx={{
                                  color: '#94a3b8',
                                  opacity: 0,
                                  transition: 'all 0.2s ease',
                                  transform: 'translateX(-8px)',
                                  '.MuiMenuItem-root:hover &': {
                                    opacity: 1,
                                    transform: 'translateX(0)',
                                    color: '#00b4d8'
                                  }
                                }}
                              />
                            </MenuItem>
                          </Select>
                        </StyledFormControl>
                      </Grid>
                    </>
                  )}
                  {(!currentRouteGroup?.id && (currentRouteGroup?.routeType === 'monthly' || currentRouteGroup?.routeType === 'bimonthly')) ? (
                    <>
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <InputLabel sx={{
                            backgroundColor: 'white',
                            padding: '0 4px',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            color: '#00b4d8'
                          }}>
                            Mes
                          </InputLabel>
                          <Select
                            value={currentRouteGroup?.month || 1}
                            onChange={(e) => setCurrentRouteGroup(currentRouteGroup ?
                              { ...currentRouteGroup, month: e.target.value as number } : null)}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8',
                                borderWidth: '2px',
                                borderRadius: '8px'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0077be'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8'
                              },
                              backgroundColor: 'white'
                            }}
                          >
                            {[
                              'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                              'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                            ].map((month, index) => (
                              <MenuItem
                                key={index + 1}
                                value={index + 1}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.08)'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.12)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 180, 216, 0.16)'
                                    }
                                  }
                                }}
                              >
                                {month}
                              </MenuItem>
                            ))}
                          </Select>
                        </StyledFormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <InputLabel sx={{
                            backgroundColor: 'white',
                            padding: '0 4px',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            color: '#00b4d8'
                          }}>
                            Día de la Semana
                          </InputLabel>
                          <Select
                            value={currentRouteGroup?.dayOfWeek || 1}
                            onChange={(e) => setCurrentRouteGroup(currentRouteGroup ?
                              { ...currentRouteGroup, dayOfWeek: e.target.value as number } : null)}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8',
                                borderWidth: '2px',
                                borderRadius: '8px'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0077be'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8'
                              },
                              backgroundColor: 'white'
                            }}
                          >
                            {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                              <MenuItem
                                key={day}
                                value={day}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.08)'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.12)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 180, 216, 0.16)'
                                    }
                                  }
                                }}
                              >
                                {getDayOfWeekName(day)}
                              </MenuItem>
                            ))}
                          </Select>
                        </StyledFormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <InputLabel sx={{
                            backgroundColor: 'white',
                            padding: '0 4px',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            color: '#00b4d8'
                          }}>
                            Semana del Mes
                          </InputLabel>
                          <Select
                            value={currentRouteGroup?.weekOfMonth || 1}
                            onChange={(e) => setCurrentRouteGroup(currentRouteGroup ?
                              { ...currentRouteGroup, weekOfMonth: e.target.value as number } : null)}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8',
                                borderWidth: '2px',
                                borderRadius: '8px'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0077be'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8'
                              },
                              backgroundColor: 'white'
                            }}
                          >
                            {[1, 2, 3, 4].map((week) => (
                              <MenuItem
                                key={week}
                                value={week}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.08)'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.12)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 180, 216, 0.16)'
                                    }
                                  }
                                }}
                              >
                                {`Semana ${week}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </StyledFormControl>
                      </Grid>
                    </>
                  ) : (currentRouteGroup?.routeType === 'monthly' || currentRouteGroup?.routeType === 'bimonthly') ? (
                    // Modo de edición: Campos de solo lectura mejorados
                    <>
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <TextField
                            label="Mes"
                            value={(() => {
                              console.log('currentRouteGroup?.month:', currentRouteGroup?.month);
                              return [
                                'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                              ][currentRouteGroup?.month ? currentRouteGroup.month - 1 : 0];
                            })()}
                            onChange={(e) => {
                              const monthIndex = [
                                'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                              ].indexOf(e.target.value) + 1;
                              if (monthIndex > 0 && currentRouteGroup) {
                                setCurrentRouteGroup({ ...currentRouteGroup, month: monthIndex });
                              }
                            }}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mr: 1
                                }}>
                                  <Box
                                    sx={{
                                      bgcolor: 'rgba(0, 180, 216, 0.1)',
                                      borderRadius: '8px',
                                      padding: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <CalendarMonth sx={{ color: '#00b4d8' }} />
                                  </Box>
                                </Box>
                              ),
                              sx: {
                                borderRadius: '8px',
                                backgroundColor: '#f8f9fa',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#f1f3f5'
                                }
                              }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8',
                                borderWidth: '2px',
                                transition: 'all 0.2s ease'
                              },
                              '& .MuiInputLabel-root': {
                                color: '#00b4d8',
                                fontWeight: 'bold'
                              },
                              '& .MuiInputBase-input': {
                                color: '#2c3e50',
                                fontWeight: 500
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0077be'
                              },
                            }}
                          />
                        </StyledFormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <TextField
                            label="Día de la Semana"
                            value={currentRouteGroup?.dayOfWeek ? getDayOfWeekName(currentRouteGroup.dayOfWeek) : 'No especificado'}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mr: 1
                                }}>
                                  <Box
                                    sx={{
                                      bgcolor: 'rgba(0, 180, 216, 0.1)',
                                      borderRadius: '8px',
                                      padding: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <Today sx={{ color: '#00b4d8' }} />
                                  </Box>
                                </Box>
                              ),
                              sx: {
                                borderRadius: '8px',
                                backgroundColor: '#f8f9fa',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#f1f3f5'
                                }
                              }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8',
                                borderWidth: '2px',
                                transition: 'all 0.2s ease'
                              },
                              '& .MuiInputLabel-root': {
                                color: '#00b4d8',
                                fontWeight: 'bold'
                              },
                              '& .MuiInputBase-input': {
                                color: '#2c3e50',
                                fontWeight: 500
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0077be'
                              },
                            }}
                          />
                        </StyledFormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <TextField
                            label="Semana del Mes"
                            value={currentRouteGroup?.weekOfMonth ? `Semana ${currentRouteGroup.weekOfMonth}` : 'No especificada'}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mr: 1
                                }}>
                                  <Box
                                    sx={{
                                      bgcolor: 'rgba(0, 180, 216, 0.1)',
                                      borderRadius: '8px',
                                      padding: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <CalendarMonth sx={{ color: '#00b4d8' }} />
                                  </Box>
                                </Box>
                              ),
                              sx: {
                                borderRadius: '8px',
                                backgroundColor: '#f8f9fa',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#f1f3f5'
                                }
                              }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00b4d8',
                                borderWidth: '2px',
                                transition: 'all 0.2s ease'
                              },
                              '& .MuiInputLabel-root': {
                                color: '#00b4d8',
                                fontWeight: 'bold'
                              },
                              '& .MuiInputBase-input': {
                                color: '#2c3e50',
                                fontWeight: 500
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0077be'
                              },
                            }}
                          />
                        </StyledFormControl>
                      </Grid>
                    </>
                  ) : null}
                  {(!currentRouteGroup?.id && (currentRouteGroup?.routeType === 'weekly' || currentRouteGroup?.routeType === 'biweekly')) ? (
                    <Grid item xs={12}>
                      <StyledFormControl fullWidth sx={{
                        backgroundColor: 'white'
                      }}>
                        <DatePicker
                          selected={currentRouteGroup?.createdAt}
                          onChange={handleCreatedAtChange}
                          selectsStart
                          dateFormat="dd/MM/yyyy"
                          customInput={
                            <TextField
                              fullWidth
                              label="Fecha de inicio"
                              InputLabelProps={{
                                sx: {
                                  backgroundColor: 'white',
                                  padding: '0 4px',
                                  fontSize: '1rem',
                                  fontWeight: 'bold',
                                  color: '#00b4d8'
                                }
                              }}
                              InputProps={{
                                readOnly: true,
                                startAdornment: (
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mr: 2.2
                                  }}>
                                    <Box
                                      sx={{
                                        bgcolor: 'rgba(0, 180, 216, 0.1)',
                                        borderRadius: '8px',
                                        padding: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Today sx={{ color: '#00b4d8' }} />
                                    </Box>
                                  </Box>
                                ),
                                sx: {
                                  padding: '9px',
                                  borderRadius: '8px',
                                  backgroundColor: '#f8f9fa',
                                  transition: 'all 0.2s ease',
                                  // '&:hover': {
                                  //   backgroundColor: '#f1f3f5'
                                  // }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#00b4d8',
                                  borderWidth: '2px',
                                  transition: 'all 0.2s ease'
                                },
                                '& .MuiInputLabel-root': {
                                  color: '#00b4d8',
                                  fontWeight: 'bold'
                                },
                                '& .MuiInputBase-input': {
                                  color: '#2c3e50',
                                  fontWeight: 500
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#0077be'
                                },
                              }}
                            />
                          }
                          locale={es}
                          popperModifiers={[
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 10],
                              },
                              fn: ({ x, y, ...rest }) => {
                                return { x, y, ...rest };
                              },
                            },
                            {
                              name: 'preventOverflow',
                              options: {
                                rootBoundary: 'viewport',
                                tether: false,
                                altAxis: true,
                              },
                              fn: ({ x, y, ...rest }) => {
                                return { x, y, ...rest };
                              },
                            },
                          ]}
                          calendarClassName="custom-calendar"
                        />
                      </StyledFormControl>
                    </Grid>
                  ) : (currentRouteGroup?.routeType === 'weekly' || currentRouteGroup?.routeType === 'biweekly') ? (
                    // Modo de edición: Campo de solo lectura mejorado
                    <Grid item xs={12}>
                      <StyledFormControl fullWidth>
                        <TextField
                          label="Fecha de inicio"
                          value={currentRouteGroup?.createdAt ?
                            new Date(currentRouteGroup.createdAt).toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            }) :
                            'No especificada'
                          }
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mr: 1
                              }}>
                                <Box
                                  sx={{
                                    bgcolor: 'rgba(0, 180, 216, 0.1)',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Today sx={{ color: '#00b4d8' }} />
                                </Box>
                              </Box>
                            ),
                            sx: {
                              borderRadius: '8px',
                              backgroundColor: '#f8f9fa',
                              transition: 'all 0.2s ease',
                              // '&:hover': {
                              //   backgroundColor: '#f1f3f5'
                              // }
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#00b4d8',
                              borderWidth: '2px',
                              transition: 'all 0.2s ease'
                            },
                            '& .MuiInputLabel-root': {
                              color: '#00b4d8',
                              fontWeight: 'bold'
                            },
                            '& .MuiInputBase-input': {
                              color: '#2c3e50',
                              fontWeight: 500
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0077be'
                            },
                          }}
                        />
                      </StyledFormControl>
                    </Grid>
                  ) : null}
                  <Grid item xs={12}>
                    <StyledFormControl fullWidth>
                      {!currentRouteGroup?.id ? (
                        // Create mode: Enhanced Autocomplete
                        <Autocomplete
                          options={transportistas}
                          value={selectedTransportista}
                          onChange={(_, newValue) => {
                            setSelectedTransportista(newValue);
                            if (currentRouteGroup && newValue) {
                              setCurrentRouteGroup({
                                ...currentRouteGroup,
                                transportistaId: newValue.id
                              } as RouteGroup);
                            }
                          }}
                          getOptionLabel={(option) => option.name}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Transportista"
                              variant="outlined"
                              placeholder="Buscar transportista..."
                              InputLabelProps={{
                                sx: {
                                  backgroundColor: 'white',
                                  padding: '0 4px',
                                  fontSize: '1rem',
                                  fontWeight: 'bold',
                                  color: '#00b4d8',
                                  transition: 'all 0.2s ease'
                                }
                              }}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <PersonIcon sx={{ color: '#00b4d8', mr: 1 }} />
                                ),
                                sx: {
                                  '& fieldset': {
                                    borderColor: '#00b4d8',
                                    borderWidth: '2px',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease'
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#0077be',
                                    borderWidth: '2px'
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#00b4d8',
                                    borderWidth: '2px'
                                  },
                                  backgroundColor: 'white'
                                }
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box
                              component="li"
                              {...props}
                              sx={{
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease', // Añadido para suavizar la transición
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.08)',
                                  transform: 'translateY(-2px)', // Añadido efecto de elevación
                                  boxShadow: '0 2px 8px rgba(0, 180, 216, 0.15)' // Añadida sombra en hover
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'rgba(0, 180, 216, 0.12)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 180, 216, 0.16)'
                                  }
                                }
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: '#00b4d8',
                                  width: 32,
                                  height: 32,
                                  fontSize: '1rem',
                                  transition: 'transform 0.2s ease', // Añadida transición para el avatar
                                  '&:hover': {
                                    transform: 'scale(1.1)' // Efecto de escala en hover
                                  }
                                }}
                              >
                                {option.name.charAt(0)}
                              </Avatar>
                              <Typography sx={{
                                fontWeight: 500,
                                color: '#2c3e50',
                                // transition: 'color 0.2s ease', // Añadida transición para el texto
                                // '&:hover': {
                                //   color: '#00b4d8' // Cambio de color del texto en hover
                                // }
                              }}>
                                {option.name}
                              </Typography>
                            </Box>
                          )}
                          noOptionsText={
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              py: 2
                            }}>
                              <SearchIcon sx={{ color: '#7f8c8d' }} />
                              <Typography sx={{
                                color: '#7f8c8d',
                                fontWeight: 500
                              }}>
                                No se encontraron transportistas
                              </Typography>
                            </Box>
                          }
                          sx={{
                            '& .MuiAutocomplete-endAdornment': {
                              color: '#00b4d8'
                            }
                          }}
                        />
                      ) : (
                        // Edit mode: Enhanced readonly display
                        <TextField
                          label="Transportista Asignado"
                          value={transportistas.find(t =>
                            t.listaRutas?.includes(currentRouteGroup?.id || '')
                          )?.name || 'No asignado'}
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mr: 1
                              }}>
                                <Avatar
                                  sx={{
                                    bgcolor: '#00b4d8',
                                    width: 32,
                                    height: 32,
                                    fontSize: '1rem'
                                  }}
                                >
                                  {(transportistas.find(t =>
                                    t.listaRutas?.includes(currentRouteGroup?.id || '')
                                  )?.name || 'N/A').charAt(0)}
                                </Avatar>
                              </Box>
                            ),
                            sx: {
                              borderRadius: '8px',
                              backgroundColor: '#f8f9fa',
                              transition: 'all 0.2s ease',
                              // '&:hover': {
                              //   backgroundColor: '#f1f3f5'
                              // }
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#00b4d8',
                              borderWidth: '2px',
                              transition: 'all 0.2s ease'
                            },
                            '& .MuiInputLabel-root': {
                              color: '#00b4d8',
                              fontWeight: 'bold'
                            },
                            '& .MuiInputBase-input': {
                              color: '#2c3e50',
                              fontWeight: 500
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0077be'
                            },
                          }}
                        />
                      )}
                    </StyledFormControl>
                  </Grid>
                </Grid>
              </InfoSection>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'right', px: 3, py: 2, backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isFormValid()}
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5CACEE',
              },
              '&:disabled': {
                backgroundColor: 'rgba(135, 206, 235, 0.5)',
                color: 'rgba(255, 255, 255, 0.7)',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}
          >
            Guardar
          </Button>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <OrderCreationModalClasicAutomatic
        open={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        inventoryItems={inventoryItems}
        existingOrder={currentRoute?.order ?? undefined}
        currentRouteGroup={currentRouteGroup || undefined} // Añadido
        currentRoute={currentRoute || undefined} // Añadido
        onOrderCreated={handleOrderCreated}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
          },
        }}>
        <DialogTitle sx={{
          backgroundColor: '#87CEEB',
          color: 'white',
          fontWeight: 'bold'
        }}>Confirmar Eliminación</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, paddingTop: '29px !important' }}>
          <Box sx={{
            backgroundColor: 'red',
            borderRadius: '50%',
            padding: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 2
          }}>
            <CloseIcon sx={{ color: 'white', fontSize: 40 }} />
          </Box>
          <Typography variant="h6" align="center" sx={{ color: '#333', fontWeight: 'bold' }}>
            ¿Está seguro de que quiere eliminar este Ruta Automatizada?
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'right',
          px: 3,
          py: 2,
          backgroundColor: 'rgba(135, 206, 235, 0.1)'
        }}>
          <Button onClick={confirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgb(255, 0, 0)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 0, 0, 0.5)',
                color: 'rgba(255, 255, 255, 0.7)',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}>
            Eliminar
          </Button>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={filterDialogOpen}
        onClose={handleFilterDialogClose}
        maxWidth="md"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#87CEEB', color: 'white', fontWeight: 'bold' }}>
          Filtrar Grupos de Rutas
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6}>
              {/* Aquí va el resto de tu código, incluyendo el ComboBox */}
              <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                <InputLabel sx={{ textAlign: 'center', width: '100%' }}>Tipo de Ruta</InputLabel>
                <Select
                  multiple
                  value={filters.routeType || []}
                  onChange={(e) => {
                    handleFilterChange('routeType', e.target.value as string[]);
                    setSelectedRouteTypes(e.target.value as string[]); // Actualiza el estado aquí
                  }}
                  input={<OutlinedInput label="Tipo de Ruta" />}
                  renderValue={(selected) => (selected as string[]).map(type => {
                    switch (type) {
                      case 'weekly': return 'Semanal';
                      case 'biweekly': return 'Bisemanal';
                      case 'monthly': return 'Mensual';
                      case 'bimonthly': return 'Bimensual';
                      default: return type;
                    }
                  }).join(', ')} // Traducción al español
                >
                  {['weekly', 'biweekly', 'monthly', 'bimonthly'].map((type) => (
                    <MenuItem key={type} value={type}>
                      <Checkbox checked={(filters.routeType || []).indexOf(type) > -1} />
                      <ListItemText primary={type === 'weekly' ? 'Semanal' :
                        type === 'biweekly' ? 'Bisemanal' :
                          type === 'monthly' ? 'Mensual' : 'Bimensual'} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'right',
          px: 3,
          py: 2,
          backgroundColor: 'rgba(135, 206, 235, 0.1)'
        }}>
          <Button
            onClick={applyFilters}
            variant="contained"
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}
          >
            Aplicar
          </Button>
          <Button
            onClick={handleFilterDialogClose}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* <Dialog
        open={columnSelectionDialogOpen}
        onClose={handleColumnSelectionDialogClose}
        PaperProps={{
          sx: {
            width: '400px',
            maxHeight: '80vh',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#87CEEB',
          color: 'white',
          fontWeight: 'bold'
        }}>
          Seleccionar Columnas
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2
          }}>
            {['name', 'routeType', 'createdAt', 'dayOfWeek', 'weekOfMonth', 'nextDelivery'].map((column) => (
              <FormControlLabel
                key={column}
                control={
                  <Checkbox
                    checked={visibleColumns.has(column)}
                    onChange={() => handleColumnToggle(column)}
                    sx={{
                      color: '#87CEEB',
                      '&.Mui-checked': {
                        color: '#87CEEB',
                      },
                    }}
                  />
                }
                label={columnNames[column as keyof typeof columnNames] || column}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(135, 206, 235, 0.08)',
                    borderRadius: '4px',
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', px: 3, py: 2, backgroundColor: 'rgba(135, 206, 235, 0.1)' }}>
          <Button
            onClick={handleColumnSelectionDialogClose}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog> */}

      {showNotification && (
        <>
          <DarkOverlay />
          <NotificationPopup>
            <AnimatedEmoji style={{ animationDelay: '0s' }}>✨</AnimatedEmoji>
            <Typography
              sx={{
                color: '#1a1a1a',
                fontWeight: 600,
                fontSize: '1.5rem',
                letterSpacing: '-0.02em',
              }}
            >
              Actualizando
            </Typography>
            <AnimatedEmoji style={{ animationDelay: '0.2s' }}>🎉</AnimatedEmoji>
          </NotificationPopup>
        </>
      )}

      {showNotificationDel && (
        <>
          <DarkOverlay />
          <NotificationPopup>
            <AnimatedEmoji style={{ animationDelay: '0s' }}>✨</AnimatedEmoji>
            <Typography
              sx={{
                color: '#1a1a1a',
                fontWeight: 600,
                fontSize: '1.5rem',
                letterSpacing: '-0.02em',
              }}
            >
              Eliminado Automatización
            </Typography>
            <AnimatedEmoji style={{ animationDelay: '0.2s' }}>🎉</AnimatedEmoji>
          </NotificationPopup>
        </>
      )}
    </Box >
  );
};

export default RouteGroupsCRUD;